import BaseService from './BaseService.js';
import { useAuth } from '../../contexts/AuthContext';
import { addDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db, functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Device Service for handling all device-related Firebase operations
 * Extends BaseService to provide device-specific functionality with caching
 */
class DeviceService extends BaseService {
  constructor() {
    super('devices');
    this.cacheTimeout = 2 * 60 * 1000; // 2 minutes for devices (more frequent updates)
    this.onlineThreshold = 5 * 60 * 1000; // 5 minutes to consider device online
  }

  /**
   * Get all devices for a specific user with caching
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of devices
   */
  async getUserDevices(uid, useCache = true) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      // Always use unsorted query first to ensure we get all devices
      // The composite index for sorting might be incomplete and missing some devices
      let devices = await this.getDocuments(
        { uid },
        null, // No sorting
        null,
        useCache
      );
      
      // Sort in memory for consistent ordering
      devices.sort((a, b) => {
        const dateA = a.created ? new Date(a.created).getTime() : 0;
        const dateB = b.created ? new Date(b.created).getTime() : 0;
        return dateB - dateA; // Descending order
      });

      // Add computed properties
      return devices.map(device => this.addComputedProperties(device));
    } catch (error) {
      console.error('Error fetching user devices:', error);
      throw error;
    }
  }

  /**
   * Get a single device by ID with caching
   * @param {string} deviceId - Device ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object|null>} Device object or null
   */
  async getDevice(deviceId, useCache = true) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      const device = await this.getDocument(deviceId, useCache);
      return device ? this.addComputedProperties(device) : null;
    } catch (error) {
      console.error('Error fetching device:', error);
      throw error;
    }
  }

  /**
   * Get device by device ID (not document key)
   * @param {string} deviceId - Device ID
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object|null>} Device object or null
   */
  async getDeviceById(deviceId, uid, useCache = true) {
    if (!deviceId || !uid) {
      throw new Error('Device ID and User ID are required');
    }

    try {
      const devices = await this.getDocuments({ uid, id: deviceId }, null, null, useCache);
      return devices.length > 0 ? this.addComputedProperties(devices[0]) : null;
    } catch (error) {
      console.error('Error fetching device by ID:', error);
      throw error;
    }
  }

  /**
   * Get device by name
   * @param {string} deviceName - Device name
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object|null>} Device object or null
   */
  async getDeviceByName(deviceName, uid, useCache = true) {
    if (!deviceName || !uid) {
      throw new Error('Device name and User ID are required');
    }

    try {
      const devices = await this.getDocuments({ uid, name: deviceName }, null, null, useCache);
      return devices.length > 0 ? this.addComputedProperties(devices[0]) : null;
    } catch (error) {
      console.error('Error fetching device by name:', error);
      throw error;
    }
  }

  /**
   * Create a new device
   * @param {Object} deviceData - Device data
   * @returns {Promise<Object>} Created device
   */
  async createDevice(deviceData) {
    if (!deviceData.uid) {
      throw new Error('User ID is required');
    }

    if (!deviceData.name || !deviceData.id) {
      throw new Error('Device name and ID are required');
    }

    try {
      // Check for duplicate device ID or name
      const existingDevices = await this.getUserDevices(deviceData.uid, false);
      
      if (existingDevices.find(dev => dev.id === deviceData.id)) {
        throw new Error(`A device with the '${deviceData.id}' ID already exists`);
      }
      
      if (existingDevices.find(dev => dev.name === deviceData.name)) {
        throw new Error(`A device with the '${deviceData.name}' name already exists`);
      }

      // Prepare device data with correct field names
      const newDeviceData = {
        ...deviceData,
        lastAccess: Date.now(),
        version: deviceData.version || '1.0.0',
        screenId: deviceData.screenId || '',
        manufacturer: deviceData.manufacturer || 'Unknown',
        model: deviceData.model || 'Unknown',
        status: 'active',
        created: Date.now() // Use 'created' instead of 'createdAt'
      };

      // Use custom create method for devices
      const createdDevice = await this.createDeviceDocument(newDeviceData);
      const deviceWithComputed = this.addComputedProperties(createdDevice);
      
      // Dispatch custom event for new device creation
      try {
        const newDeviceEvent = new CustomEvent('new-device-added', {
          detail: {
            deviceId: deviceWithComputed.key,
            deviceName: deviceWithComputed.name,
            deviceData: deviceWithComputed,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(newDeviceEvent);
        console.log('📡 Dispatched new-device-added event for:', deviceWithComputed.name);
      } catch (eventError) {
        console.warn('Could not dispatch new device event:', eventError);
      }
      
      return deviceWithComputed;
    } catch (error) {
      console.error('Error creating device:', error);
      throw error;
    }
  }

  /**
   * Create a device document with correct field names
   * @param {Object} data - Device data
   * @returns {Promise<Object>} Created device
   */
  async createDeviceDocument(data) {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...data,
        created: data.created || Date.now() // Use 'created' field
      });

      // Clear relevant cache
      this.clearCache();

      return {
        key: docRef.id,
        ...data,
        created: data.created || Date.now()
      };
    } catch (error) {
      console.error(`Error creating device document:`, error);
      throw error;
    }
  }

  /**
   * Update an existing device
   * @param {string} deviceId - Device document ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated device
   */
  async updateDevice(deviceId, updateData) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      // If updating name, check for duplicates
      if (updateData.name) {
        const currentDevice = await this.getDevice(deviceId, false);
        if (currentDevice) {
          const existingDevices = await this.getUserDevices(currentDevice.uid, false);
          const duplicate = existingDevices.find(dev => 
            dev.name === updateData.name && dev.key !== deviceId
          );
          
          if (duplicate) {
            throw new Error(`A device with the '${updateData.name}' name already exists`);
          }
        }
      }

      // Handle mutual exclusivity between screenId and channelId
      if (updateData.screenId !== undefined) {
        // If setting a screenId, clear channelId
        if (updateData.screenId && updateData.screenId !== '') {
          updateData.channelId = '';
        }
      } else if (updateData.channelId !== undefined) {
        // If setting a channelId, clear screenId
        if (updateData.channelId && updateData.channelId !== '') {
          updateData.screenId = '';
        }
      }

      const updatedDevice = await this.updateDocument(deviceId, updateData);
      return this.addComputedProperties(updatedDevice);
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  }

  /**
   * Delete a device
   * @param {string} deviceId - Device document ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteDevice(deviceId) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      await this.deleteDocument(deviceId);
      return true;
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  }

  /**
   * Update device's last access time (ping)
   * @param {string} deviceId - Device document ID
   * @param {string} version - Device version (optional)
   * @returns {Promise<Object>} Updated device
   */
  async pingDevice(deviceId, version = null) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      const updateData = {
        lastAccess: Date.now()
      };

      if (version) {
        updateData.version = version;
      }

      const updatedDevice = await this.updateDocument(deviceId, updateData);
      return this.addComputedProperties(updatedDevice);
    } catch (error) {
      console.error('Error pinging device:', error);
      throw error;
    }
  }

  /**
   * Get online devices for a user
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of online devices
   */
  async getOnlineDevices(uid, useCache = true) {
    try {
      const devices = await this.getUserDevices(uid, useCache);
      return devices.filter(device => device.isOnline);
    } catch (error) {
      console.error('Error fetching online devices:', error);
      throw error;
    }
  }

  /**
   * Get offline devices for a user
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of offline devices
   */
  async getOfflineDevices(uid, useCache = true) {
    try {
      const devices = await this.getUserDevices(uid, useCache);
      return devices.filter(device => !device.isOnline);
    } catch (error) {
      console.error('Error fetching offline devices:', error);
      throw error;
    }
  }

  /**
   * Get devices by status
   * @param {string} uid - User ID
   * @param {string} status - Device status
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of devices with specified status
   */
  async getDevicesByStatus(uid, status, useCache = true) {
    try {
      const devices = await this.getUserDevices(uid, useCache);
      return devices.filter(device => device.status === status);
    } catch (error) {
      console.error('Error fetching devices by status:', error);
      throw error;
    }
  }

  /**
   * Get devices assigned to a specific screen
   * @param {string} uid - User ID
   * @param {string} screenId - Screen ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of devices assigned to the screen
   */
  async getDevicesByScreen(uid, screenId, useCache = true) {
    try {
      const devices = await this.getUserDevices(uid, useCache);
      return devices.filter(device => device.screenId === screenId);
    } catch (error) {
      console.error('Error fetching devices by screen:', error);
      throw error;
    }
  }

  /**
   * Assign a screen to a device
   * @param {string} deviceId - Device document ID
   * @param {string} screenId - Screen ID
   * @returns {Promise<Object>} Updated device
   */
  async assignScreenToDevice(deviceId, screenId) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      // Clear channelId and set screenId
      const updatedDevice = await this.updateDocument(deviceId, { 
        screenId: screenId,
        channelId: '' // Clear channel assignment
      });
      return this.addComputedProperties(updatedDevice);
    } catch (error) {
      console.error('Error assigning screen to device:', error);
      throw error;
    }
  }

  /**
   * Remove screen from device
   * @param {string} deviceId - Device document ID
   * @returns {Promise<Object>} Updated device
   */
  async removeScreenFromDevice(deviceId) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      const updatedDevice = await this.updateDocument(deviceId, { screenId: '' });
      return this.addComputedProperties(updatedDevice);
    } catch (error) {
      console.error('Error removing screen from device:', error);
      throw error;
    }
  }

  /**
   * Assign channel to device (clears screenId)
   * @param {string} deviceId - Device document ID
   * @param {string} channelId - Channel document ID
   * @returns {Promise<Object>} Updated device
   */
  async assignChannelToDevice(deviceId, channelId) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      // Clear screenId and set channelId
      const updatedDevice = await this.updateDocument(deviceId, { 
        channelId: channelId,
        screenId: '' // Clear screen assignment
      });
      return this.addComputedProperties(updatedDevice);
    } catch (error) {
      console.error('Error assigning channel to device:', error);
      throw error;
    }
  }

  /**
   * Remove channel from device
   * @param {string} deviceId - Device document ID
   * @returns {Promise<Object>} Updated device
   */
  async removeChannelFromDevice(deviceId) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      const updatedDevice = await this.updateDocument(deviceId, { channelId: '' });
      return this.addComputedProperties(updatedDevice);
    } catch (error) {
      console.error('Error removing channel from device:', error);
      throw error;
    }
  }

  /**
   * Get devices assigned to a specific channel
   * @param {string} uid - User ID
   * @param {string} channelId - Channel document ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of devices
   */
  async getDevicesByChannel(uid, channelId, useCache = true) {
    try {
      const devices = await this.getDocuments({ uid, channelId }, null, null, useCache);
      return devices.map(device => this.addComputedProperties(device));
    } catch (error) {
      console.error('Error fetching devices by channel:', error);
      throw error;
    }
  }

  /**
   * Get device statistics for a user
   * @param {string} uid - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} Device statistics
   */
  async getDeviceStats(uid, useCache = true) {
    try {
      const devices = await this.getUserDevices(uid, useCache);
      
      const stats = {
        total: devices.length,
        online: devices.filter(d => d.isOnline).length,
        offline: devices.filter(d => !d.isOnline).length,
        active: devices.filter(d => d.status === 'active').length,
        inactive: devices.filter(d => d.status === 'inactive').length,
        screenAssigned: devices.filter(d => d.screenId && d.screenId !== '').length,
        channelAssigned: devices.filter(d => d.channelId && d.channelId !== '').length,
        unassigned: devices.filter(d => (!d.screenId || d.screenId === '') && (!d.channelId || d.channelId === '')).length,
        byManufacturer: {},
        byModel: {}
      };

      // Group by manufacturer and model
      devices.forEach(device => {
        const manufacturer = device.manufacturer || 'Unknown';
        const model = device.model || 'Unknown';
        
        stats.byManufacturer[manufacturer] = (stats.byManufacturer[manufacturer] || 0) + 1;
        stats.byModel[model] = (stats.byModel[model] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching device statistics:', error);
      throw error;
    }
  }

  /**
   * Add computed properties to device object
   * @param {Object} device - Device object
   * @returns {Object} Device object with computed properties
   */
  addComputedProperties(device) {
    if (!device) return null;

    const now = Date.now();
    const lastAccess = device.lastAccess || 0;
    
    return {
      ...device,
      // Online status (device is considered online if last access was within 5 minutes)
      isOnline: lastAccess > 0 && (now - lastAccess) < this.onlineThreshold,
      
      // Formatted last access time
      lastAccessFormatted: lastAccess > 0 ? new Date(lastAccess).toLocaleString() : 'Never',
      
      // Device age (time since creation)
      age: device.created ? Math.floor((now - device.created) / (1000 * 60 * 60 * 24)) : 0,
      
      // Status indicator
      statusIndicator: device.status || 'active',
      
      // Short device ID (without user suffix)
      shortId: device.id ? device.id.split('_')[0] : device.id,
      
      // Schedule information
      hasSchedule: device.schedule && device.schedule.length > 0,
      scheduleCount: device.schedule ? device.schedule.length : 0,
      
      // Screen assignment status
      hasAssignedScreen: !!(device.screenId && device.screenId !== ''),
      
      // Device info summary
      deviceInfo: `${device.manufacturer || 'Unknown'} ${device.model || 'Unknown'}`.trim(),
      
      // Version info
      versionInfo: device.version || 'Unknown',
      
      // Legacy compatibility
      lastSeen: lastAccess > 0 ? new Date(lastAccess).toISOString() : null,
      offlineDuration: lastAccess > 0 ? Math.max(0, now - lastAccess) : null,
      hasScreen: !!(device.screenId && device.screenId !== ''),
      status: device.status || 'active'
    };
  }

  /**
   * Set up real-time listener for user's devices
   * @param {string} uid - User ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToUserDevices(uid, callback) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    try {
      // Always use unsorted query for real-time subscription to ensure we get all devices
      // The composite index for sorting might be incomplete and missing some devices
      return this.subscribeToDocuments(
        { uid },
        (devices, changes) => {
          // Sort devices by created in memory for consistency
          const sortedDevices = devices.sort((a, b) => {
            const dateA = a.created ? new Date(a.created).getTime() : 0;
            const dateB = b.created ? new Date(b.created).getTime() : 0;
            return dateB - dateA; // Descending order
          });
          
          const devicesWithComputed = sortedDevices.map(device => this.addComputedProperties(device));
          callback(devicesWithComputed, changes);
        },
        {} // No sorting options
      );
    } catch (error) {
      console.error('Error setting up device subscription:', error);
      throw error;
    }
  }

  /**
   * Set up real-time listener for a specific device
   * @param {string} deviceId - Device document ID
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribeToDevice(deviceId, callback) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    return this.subscribeToDocument(deviceId, (device, change) => {
      const deviceWithComputed = device ? this.addComputedProperties(device) : null;
      callback(deviceWithComputed, change);
    });
  }

  /**
   * Set up real-time listener for screen assignment changes on a specific device
   * @param {string} deviceId - Device document ID
   * @param {Function} callback - Callback function called when screen assignment changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToDeviceScreenAssignment(deviceId, callback) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    return this.subscribeToDocument(deviceId, (device, change) => {
      if (device && change.type === 'modified') {
        // Check if screenId field changed
        const oldScreenId = change.oldDoc?.screenId || '';
        const newScreenId = device.screenId || '';
        
        if (oldScreenId !== newScreenId) {
          const deviceWithComputed = this.addComputedProperties(device);
          callback(deviceWithComputed, {
            type: 'screen_assignment_changed',
            oldScreenId,
            newScreenId,
            device: deviceWithComputed
          });
        }
      }
    });
  }

  /**
   * Set up real-time listener for screen assignment changes across all user devices
   * @param {string} uid - User ID
   * @param {Function} callback - Callback function called when any device's screen assignment changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToUserDeviceScreenAssignments(uid, callback) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    return this.subscribeToDocuments(
      { uid },
      (devices, changes) => {
        // Filter for screen assignment changes
        const screenAssignmentChanges = changes.filter(change => {
          if (change.type === 'modified' && change.oldDoc && change.newDoc) {
            const oldScreenId = change.oldDoc.screenId || '';
            const newScreenId = change.newDoc.screenId || '';
            return oldScreenId !== newScreenId;
          }
          return false;
        });

        if (screenAssignmentChanges.length > 0) {
          const devicesWithComputed = devices.map(device => this.addComputedProperties(device));
          callback(devicesWithComputed, screenAssignmentChanges);
        }
      },
      { sortBy: { field: 'created', direction: 'desc' } }
    );
  }

  /**
   * Set up real-time listener for device online status changes (lastAccess field)
   * @param {string} uid - User ID
   * @param {Function} callback - Callback function called when device online status changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToDeviceOnlineStatus(uid, callback) {
    if (!uid) {
      throw new Error('User ID is required');
    }

    // Track previous device states to detect online status changes
    let previousDevices = new Map();
    // Track timers for each device
    let deviceTimers = new Map();


    return this.subscribeToDocuments(
      { uid },
      (devices, changes) => {
        const now = Date.now();
        const onlineStatusChanges = [];

        // Process each device to detect online status changes
        devices.forEach(device => {
          const deviceWithComputed = this.addComputedProperties(device);
          const previousDevice = previousDevices.get(device.key);
          
          // Clear existing timer for this device if it exists
          if (deviceTimers.has(device.key)) {
            clearTimeout(deviceTimers.get(device.key));
            deviceTimers.delete(device.key);
          }
          
          // Set up timer to check device status after 330 seconds if device is online
          if (deviceWithComputed.lastAccess && deviceWithComputed.isOnline) {
            const timerId = setTimeout(async () => {
              console.log('⏰ Timer fired for device:', deviceWithComputed.name, 'checking if still online...');
              
              try {
                // Fetch fresh device data
                const freshDevice = await this.getDevice(device.key, false);
                const freshDeviceWithComputed = this.addComputedProperties(freshDevice);
                const currentTime = Date.now();
                
                // Check if device is still online
                const isStillOnline = freshDeviceWithComputed.lastAccess > 0 && 
                  (currentTime - freshDeviceWithComputed.lastAccess) < this.onlineThreshold;
                
                console.log('🔍 Timer check - Device:', freshDeviceWithComputed.name, 
                  'lastAccess:', freshDeviceWithComputed.lastAccess,
                  'timeSinceLastAccess:', currentTime - freshDeviceWithComputed.lastAccess,
                  'isStillOnline:', isStillOnline);
                
                // Check if device status changed (online to offline OR offline to online)
                if (isStillOnline !== deviceWithComputed.isOnline) {
                  const oldStatus = deviceWithComputed.isOnline ? 'online' : 'offline';
                  const newStatus = isStillOnline ? 'online' : 'offline';
                  
                  console.log('📱 Timer detected device status change:', freshDeviceWithComputed.name, oldStatus, '→', newStatus);
                  
                  const statusChange = {
                    type: 'online_status_changed',
                    oldDoc: deviceWithComputed,
                    newDoc: freshDeviceWithComputed,
                    oldStatus: oldStatus,
                    newStatus: newStatus,
                    device: freshDeviceWithComputed,
                    triggeredBy: 'timer'
                  };
                  
                  // Update the devices array with the new status
                  const updatedDevices = devices.map(d => 
                    d.key === freshDeviceWithComputed.key ? freshDeviceWithComputed : d
                  );
                  
                  callback(updatedDevices, [statusChange]);
                  
                  // Dispatch custom event to notify other components of device status change
                  const deviceStatusEvent = new CustomEvent('device-status-changed', {
                    detail: {
                      deviceId: freshDeviceWithComputed.key,
                      deviceName: freshDeviceWithComputed.name,
                      oldStatus: oldStatus,
                      newStatus: newStatus,
                      triggeredBy: 'timer',
                      timestamp: Date.now()
                    }
                  });
                  window.dispatchEvent(deviceStatusEvent);
                  console.log('📡 Dispatched device-status-changed event for:', freshDeviceWithComputed.name, oldStatus, '→', newStatus);
                }
              } catch (error) {
                console.error('❌ Error checking device status in timer:', error);
              }
            }, 330000); // 330 seconds = 5.5 minutes
            
            deviceTimers.set(device.key, timerId);
            console.log('⏰ Set timer for device:', deviceWithComputed.name, 'will check in 330 seconds');
          }
          
          if (previousDevice) {
            // Check if online status changed
            if (previousDevice.isOnline !== deviceWithComputed.isOnline) {
              onlineStatusChanges.push({
                type: 'online_status_changed',
                oldDoc: previousDevice,
                newDoc: deviceWithComputed,
                oldStatus: previousDevice.isOnline ? 'online' : 'offline',
                newStatus: deviceWithComputed.isOnline ? 'online' : 'offline',
                device: deviceWithComputed
              });
            }
          } else if (deviceWithComputed.lastAccess) {
            // New device, check if it's online
            const isOnline = deviceWithComputed.lastAccess > 0 && (now - deviceWithComputed.lastAccess) < this.onlineThreshold;
            if (isOnline !== deviceWithComputed.isOnline) {
              onlineStatusChanges.push({
                type: 'online_status_changed',
                oldDoc: null,
                newDoc: deviceWithComputed,
                oldStatus: 'unknown',
                newStatus: deviceWithComputed.isOnline ? 'online' : 'offline',
                device: deviceWithComputed
              });
            }
          }
          
          // Update previous state
          previousDevices.set(device.key, deviceWithComputed);
        });

        // Remove devices that no longer exist
        const currentDeviceKeys = new Set(devices.map(d => d.key));
        for (const [key, device] of previousDevices.entries()) {
          if (!currentDeviceKeys.has(key)) {
            previousDevices.delete(key);
            // Clear timer for removed device
            if (deviceTimers.has(key)) {
              clearTimeout(deviceTimers.get(key));
              deviceTimers.delete(key);
            }
          }
        }

        if (onlineStatusChanges.length > 0) {
          console.log('🔍 DeviceService - Online status changes detected:', onlineStatusChanges.length, 'changes');
          onlineStatusChanges.forEach(change => {
            console.log('📱 Device status change:', change.device.name, change.oldStatus, '→', change.newStatus, 'lastAccess:', change.device.lastAccess);
            
            // Dispatch custom event for each status change
            const deviceStatusEvent = new CustomEvent('device-status-changed', {
              detail: {
                deviceId: change.device.key,
                deviceName: change.device.name,
                oldStatus: change.oldStatus,
                newStatus: change.newStatus,
                triggeredBy: 'realtime',
                timestamp: Date.now()
              }
            });
            window.dispatchEvent(deviceStatusEvent);
            console.log('📡 Dispatched device-status-changed event for:', change.device.name, change.oldStatus, '→', change.newStatus);
          });
          const devicesWithComputed = devices.map(device => this.addComputedProperties(device));
          callback(devicesWithComputed, onlineStatusChanges);
        }
      },
      { sortBy: { field: 'created', direction: 'desc' } }
    );
  }

  /**
   * Set up real-time listener for a specific device's online status changes
   * @param {string} deviceId - Device document ID
   * @param {Function} callback - Callback function called when device online status changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToDeviceOnlineStatusChange(deviceId, callback) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    return this.subscribeToDocument(deviceId, (device, change) => {
      if (device && change.type === 'modified' && change.oldDoc && change.newDoc) {
        const oldLastAccess = change.oldDoc.lastAccess || 0;
        const newLastAccess = change.newDoc.lastAccess || 0;
        
        // Check if lastAccess changed
        if (oldLastAccess !== newLastAccess) {
          const now = Date.now();
          const oldWasOnline = oldLastAccess > 0 && (now - oldLastAccess) < this.onlineThreshold;
          const newIsOnline = newLastAccess > 0 && (now - newLastAccess) < this.onlineThreshold;
          
          // Only trigger if online status actually changed
          if (oldWasOnline !== newIsOnline) {
            const deviceWithComputed = this.addComputedProperties(device);
            callback(deviceWithComputed, {
              type: 'online_status_changed',
              oldStatus: oldWasOnline ? 'online' : 'offline',
              newStatus: newIsOnline ? 'online' : 'offline',
              oldLastAccess,
              newLastAccess,
              device: deviceWithComputed
            });
          }
        }
      }
    });
  }

  /**
   * Batch update multiple devices
   * @param {Array} operations - Array of update operations
   * @returns {Promise<boolean>} Success status
   */
  async batchUpdateDevices(operations) {
    try {
      const batchOperations = operations.map(op => ({
        type: 'update',
        id: op.deviceId,
        data: op.updateData
      }));

      await this.batchOperation(batchOperations);
      return true;
    } catch (error) {
      console.error('Error in batch update devices:', error);
      throw error;
    }
  }

  /**
   * Clean up old offline devices (optional maintenance function)
   * @param {string} uid - User ID
   * @param {number} daysThreshold - Number of days to consider device old
   * @returns {Promise<number>} Number of devices cleaned up
   */
  async cleanupOldOfflineDevices(uid, daysThreshold = 30) {
    try {
      const devices = await this.getUserDevices(uid, false);
      const threshold = Date.now() - (daysThreshold * 24 * 60 * 60 * 1000);
      
      const oldOfflineDevices = devices.filter(device => 
        !device.isOnline && device.lastAccess < threshold
      );

      if (oldOfflineDevices.length > 0) {
        const deleteOperations = oldOfflineDevices.map(device => ({
          type: 'delete',
          id: device.key
        }));

        await this.batchOperation(deleteOperations);
      }

      return oldOfflineDevices.length;
    } catch (error) {
      console.error('Error cleaning up old offline devices:', error);
      throw error;
    }
  }

  /**
   * Get information about required Firestore indexes
   * @returns {Object} Index information for manual creation
   */
  getRequiredIndexes() {
    return {
      devices: {
        collection: 'devices',
        indexes: [
          {
            name: 'uid_created_desc',
            fields: [
              { fieldPath: 'uid', order: 'ASCENDING' },
              { fieldPath: 'created', order: 'DESCENDING' }
            ],
            description: 'For efficient querying of user devices sorted by creation date'
          },
          {
            name: 'uid_id',
            fields: [
              { fieldPath: 'uid', order: 'ASCENDING' },
              { fieldPath: 'id', order: 'ASCENDING' }
            ],
            description: 'For efficient querying of devices by user and device ID'
          },
          {
            name: 'uid_name',
            fields: [
              { fieldPath: 'uid', order: 'ASCENDING' },
              { fieldPath: 'name', order: 'ASCENDING' }
            ],
            description: 'For efficient querying of devices by user and device name'
          }
        ]
      }
    };
  }

  /**
   * Log index creation instructions to console
   */
  logIndexInstructions() {
    const indexes = this.getRequiredIndexes();
    console.group('🔧 Firestore Index Creation Instructions');
    console.log('To improve performance, create the following composite indexes in Firebase Console:');
    
    indexes.devices.indexes.forEach(index => {
      console.group(`📋 ${index.name}`);
      console.log(`Collection: ${indexes.devices.collection}`);
      console.log(`Description: ${index.description}`);
      console.log('Fields:');
      index.fields.forEach(field => {
        console.log(`  - ${field.fieldPath}: ${field.order}`);
      });
      console.groupEnd();
    });
    
    console.log('\n📝 Steps to create indexes:');
    console.log('1. Go to Firebase Console > Firestore Database > Indexes');
    console.log('2. Click "Add Index"');
    console.log('3. Select collection: "devices"');
    console.log('4. Add fields as shown above');
    console.log('5. Click "Create Index"');
    console.log('6. Wait for index to build (may take a few minutes)');
    console.groupEnd();
  }

  /**
   * Delete a schedule from a device
   * @param {string} deviceId - Device document ID
   * @param {Object} schedule - Schedule object to delete
   * @returns {Promise<Object>} Updated device
   */
  async deleteSchedule(deviceId, schedule) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    if (!schedule || !schedule.id) {
      throw new Error('Schedule with ID is required');
    }

    try {
      // Get current device to access its schedules
      const device = await this.getDevice(deviceId, false);
      if (!device) {
        throw new Error('Device not found');
      }

      // Filter out the schedule to delete
      const updatedSchedules = device.schedule ? device.schedule.filter(sch => sch.id !== schedule.id) : [];

      // Update device with new schedule array
      const updatedDevice = await this.updateDevice(deviceId, { schedule: updatedSchedules });
      return updatedDevice;
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw error;
    }
  }

  /**
   * Update device schedules (add, modify, or replace all schedules)
   * @param {string} deviceId - Device document ID
   * @param {Array} schedules - New schedule array
   * @returns {Promise<Object>} Updated device
   */
  async updateDeviceSchedules(deviceId, schedules) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    if (!Array.isArray(schedules)) {
      throw new Error('Schedules must be an array');
    }

    try {
      const updatedDevice = await this.updateDevice(deviceId, { schedule: schedules });
      return updatedDevice;
    } catch (error) {
      console.error('Error updating device schedules:', error);
      throw error;
    }
  }

  /**
   * Create a new schedule for a device (regular or repetitive)
   * @param {string} deviceId - Device document ID
   * @param {Object} scheduleData - Schedule data object
   * @param {string} scheduleData.type - 'regular' or 'repetitive'
   * @param {string} scheduleData.screenId - Screen ID to display
   * @param {string} scheduleData.startDate - Start date (for regular schedules)
   * @param {string} scheduleData.endDate - End date (for regular schedules)
   * @param {Array} scheduleData.days - Days configuration (for repetitive schedules)
   * @returns {Promise<Object>} Updated device with new schedule
   */
  async setSchedule(deviceId, scheduleData) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    if (!scheduleData) {
      throw new Error('Schedule data is required');
    }

    if (!scheduleData.type || !['regular', 'repetitive'].includes(scheduleData.type)) {
      throw new Error('Schedule type must be "regular" or "repetitive"');
    }

    if (!scheduleData.screenId) {
      throw new Error('Screen ID is required');
    }

    try {
      // Get current device to access its schedules
      const device = await this.getDevice(deviceId, false);
      if (!device) {
        throw new Error('Device not found');
      }

      const currentSchedules = device.schedule || [];
      let newSchedule;

      if (scheduleData.type === 'regular') {
        // Validate regular schedule data
        if (!scheduleData.startDate || !scheduleData.endDate) {
          throw new Error('Both start and end date/time are required for regular schedules');
        }

        const startTimestamp = new Date(scheduleData.startDate).getTime();
        const endTimestamp = new Date(scheduleData.endDate).getTime();

        if (endTimestamp <= startTimestamp) {
          throw new Error('End date/time must be after start date/time');
        }

        // Check for schedule overlaps
        const overlapError = this.checkScheduleOverlap(currentSchedules, {
          start: startTimestamp,
          end: endTimestamp,
          isRepetitive: false
        });
        if (overlapError) {
          throw new Error(overlapError);
        }

        // Create regular schedule
        const id = `${scheduleData.screenId}_${startTimestamp}`;
        newSchedule = {
          id,
          start: startTimestamp,
          end: endTimestamp,
          isRepetitive: false,
          screenId: scheduleData.screenId
        };

      } else if (scheduleData.type === 'repetitive') {
        // Validate repetitive schedule data
        if (!scheduleData.days || !Array.isArray(scheduleData.days)) {
          throw new Error('Days configuration is required for repetitive schedules');
        }

        const enabledDays = scheduleData.days.filter(d => d.enabled);
        if (enabledDays.length === 0) {
          throw new Error('At least one day must be enabled');
        }

        // Validate time ranges for each enabled day
        for (const day of enabledDays) {
          if (day.endHour <= day.startHour) {
            throw new Error(`End hour must be after start hour for ${day.name}`);
          }
        }

        // Check for schedule overlaps
        const overlapError = this.checkScheduleOverlap(currentSchedules, {
          isRepetitive: true,
          days: scheduleData.days
        });
        if (overlapError) {
          throw new Error(overlapError);
        }

        // Create repetitive schedule
        const id = `${scheduleData.screenId}_${Date.now()}`;
        newSchedule = {
          id,
          isRepetitive: true,
          screenId: scheduleData.screenId,
          days: scheduleData.days
        };
      }

      // Add new schedule to existing schedules
      const updatedSchedules = [...currentSchedules, newSchedule];

      // Update device with new schedule array
      const updatedDevice = await this.updateDevice(deviceId, { schedule: updatedSchedules });
      return updatedDevice;

    } catch (error) {
      console.error('Error setting schedule:', error);
      throw error;
    }
  }

  /**
   * Check for schedule overlaps
   * @param {Array} existingSchedules - Current device schedules
   * @param {Object} newSchedule - New schedule to check
   * @returns {string|null} Error message if overlap found, null otherwise
   */
  checkScheduleOverlap(existingSchedules, newSchedule) {
    if (!existingSchedules || existingSchedules.length === 0) {
      return null; // No existing schedules to check against
    }

    if (newSchedule.isRepetitive) {
      // Check overlap with existing repetitive schedules
      for (const existingSchedule of existingSchedules) {
        if (existingSchedule.isRepetitive) {
          // Check if any enabled days overlap
          const hasOverlap = newSchedule.days.some(newDay => {
            if (!newDay.enabled) return false;
            
            const existingDay = existingSchedule.days.find(d => d.index === newDay.index);
            if (!existingDay || !existingDay.enabled) return false;
            
            // Check time overlap
            const newStart = newDay.startHour;
            const newEnd = newDay.endHour;
            const existingStart = existingDay.startHour;
            const existingEnd = existingDay.endHour;
            
            return (newStart < existingEnd && newEnd > existingStart);
          });
          
          if (hasOverlap) {
            return 'Schedule overlaps with existing repetitive schedule';
          }
        } else {
          // Check overlap with existing regular schedule
          const hasOverlap = newSchedule.days.some(day => {
            if (!day.enabled) return false;
            
            // Convert regular schedule to day of week and check if it falls on enabled days
            const regularStartDate = new Date(existingSchedule.start);
            const regularEndDate = new Date(existingSchedule.end);
            const startDayOfWeek = regularStartDate.getDay();
            const endDayOfWeek = regularEndDate.getDay();
            
            // Check if the regular schedule spans multiple days
            if (regularStartDate.toDateString() !== regularEndDate.toDateString()) {
              // Multi-day schedule - check if any day in range is enabled
              const currentDate = new Date(regularStartDate);
              while (currentDate <= regularEndDate) {
                const dayOfWeek = currentDate.getDay();
                const matchingDay = newSchedule.days.find(d => d.index === dayOfWeek);
                if (matchingDay && matchingDay.enabled) {
                  // Check time overlap
                  const regularStartHour = regularStartDate.getHours();
                  const regularEndHour = regularEndDate.getHours();
                  const repetitiveStartHour = matchingDay.startHour;
                  const repetitiveEndHour = matchingDay.endHour;
                  
                  if (regularStartHour < repetitiveEndHour && regularEndHour > repetitiveStartHour) {
                    return true; // Overlap found
                  }
                }
                currentDate.setDate(currentDate.getDate() + 1);
              }
              return false;
            } else {
              // Single day schedule
              const matchingDay = newSchedule.days.find(d => d.index === startDayOfWeek);
              if (matchingDay && matchingDay.enabled) {
                // Check time overlap
                const regularStartHour = regularStartDate.getHours();
                const regularEndHour = regularEndDate.getHours();
                const repetitiveStartHour = matchingDay.startHour;
                const repetitiveEndHour = matchingDay.endHour;
                
                return (regularStartHour < repetitiveEndHour && regularEndHour > repetitiveStartHour);
              }
            }
            return false;
          });
          
          if (hasOverlap) {
            return 'Schedule overlaps with existing regular schedule';
          }
        }
      }
    } else {
      // Check overlap with existing schedules for regular schedule
      for (const existingSchedule of existingSchedules) {
        if (existingSchedule.isRepetitive) {
          // Check overlap with repetitive schedule
          const hasOverlap = existingSchedule.days.some(day => {
            if (!day.enabled) return false;
            
            // Convert new schedule to day of week and check if it falls on enabled days
            const newStartDate = new Date(newSchedule.start);
            const newEndDate = new Date(newSchedule.end);
            const startDayOfWeek = newStartDate.getDay();
            const endDayOfWeek = newEndDate.getDay();
            
            // Check if the schedule spans multiple days
            if (newStartDate.toDateString() !== newEndDate.toDateString()) {
              // Multi-day schedule - check if any day in range is enabled
              const currentDate = new Date(newStartDate);
              while (currentDate <= newEndDate) {
                const dayOfWeek = currentDate.getDay();
                if (day.index === dayOfWeek) {
                  // Check time overlap
                  const newStartHour = newStartDate.getHours();
                  const newEndHour = newEndDate.getHours();
                  const repetitiveStartHour = day.startHour;
                  const repetitiveEndHour = day.endHour;
                  
                  if (newStartHour < repetitiveEndHour && newEndHour > repetitiveStartHour) {
                    return true; // Overlap found
                  }
                }
                currentDate.setDate(currentDate.getDate() + 1);
              }
              return false;
            } else {
              // Single day schedule
              if (day.index === startDayOfWeek) {
                // Check time overlap
                const newStartHour = newStartDate.getHours();
                const newEndHour = newEndDate.getHours();
                const repetitiveStartHour = day.startHour;
                const repetitiveEndHour = day.endHour;
                
                return (newStartHour < repetitiveEndHour && newEndHour > repetitiveStartHour);
              }
            }
            return false;
          });
          
          if (hasOverlap) {
            return 'Schedule overlaps with existing repetitive schedule';
          }
        } else {
          // Check overlap with regular schedule
          const newStart = newSchedule.start;
          const newEnd = newSchedule.end;
          const existingStart = existingSchedule.start;
          const existingEnd = existingSchedule.end;
          
          if (newStart < existingEnd && newEnd > existingStart) {
            return 'Schedule overlaps with existing regular schedule';
          }
        }
      }
    }

    return null; // No overlaps found
  }

  /**
   * Get schedules for a specific device
   * @param {string} deviceId - Device document ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Array>} Array of schedules
   */
  async getDeviceSchedules(deviceId, useCache = true) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    try {
      const device = await this.getDevice(deviceId, useCache);
      if (!device) {
        return [];
      }

      return device.schedule || [];
    } catch (error) {
      console.error('Error getting device schedules:', error);
      throw error;
    }
  }

  /**
   * Debug method to check raw data in the devices collection
   * @param {string} uid - User ID (optional, if not provided checks all devices)
   * @returns {Promise<Array>} Raw devices data
   */
  async debugGetAllDevices(uid = null) {
    console.log('🔍 Debug: Getting all devices from collection...');
    
    try {
      let devices;
      if (uid) {
        console.log('🔍 Debug: Filtering by uid:', uid);
        devices = await this.getDocuments({ uid }, null, null, false);
      } else {
        console.log('🔍 Debug: Getting all devices without filter');
        devices = await this.getDocuments({}, null, null, false);
      }
      
      console.log('🔍 Debug: Raw devices found:', devices);
      console.log('🔍 Debug: Devices count:', devices.length);
      
      if (devices.length > 0) {
        console.log('🔍 Debug: Sample device structure:', devices[0]);
      }
      
      return devices;
    } catch (error) {
      console.error('🔍 Debug: Error getting all devices:', error);
      throw error;
    }
  }

  /**
   * Update an existing schedule in a device
   * @param {string} deviceId - Device document ID
   * @param {string} scheduleId - Schedule ID to update
   * @param {Object} updatedSchedule - Updated schedule data
   * @returns {Promise<Object>} Updated device
   */
  async updateSchedule(deviceId, scheduleId, updatedSchedule) {
    if (!deviceId) {
      throw new Error('Device ID is required');
    }

    if (!scheduleId) {
      throw new Error('Schedule ID is required');
    }

    if (!updatedSchedule) {
      throw new Error('Updated schedule data is required');
    }

    try {
      // Get current device to access its schedules
      const device = await this.getDevice(deviceId, false);
      if (!device) {
        throw new Error('Device not found');
      }

      // Find and update the specific schedule
      const currentSchedules = device.schedule || [];
      const updatedSchedules = currentSchedules.map(schedule => 
        schedule.id === scheduleId ? { ...schedule, ...updatedSchedule } : schedule
      );

      // Check if schedule was found and updated
      const scheduleExists = currentSchedules.some(schedule => schedule.id === scheduleId);
      if (!scheduleExists) {
        throw new Error('Schedule not found');
      }

      // Update device with new schedule array
      const updatedDevice = await this.updateDevice(deviceId, { schedule: updatedSchedules });
      return updatedDevice;
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  }

  /**
   * Create a custom token for device authentication
   * @param {string} devicePin - Device PIN code
   * @returns {Promise<Object>} Result with token information
   */
  async createCustomToken(devicePin) {
    if (!devicePin) {
      throw new Error('Device PIN is required');
    }

    try {
      // First check if the device PIN exists in deviceLoginTokens collection
      const deviceLoginTokensRef = collection(db, 'deviceLoginTokens');
      const deviceTokenQuery = query(deviceLoginTokensRef, where('__name__', '==', devicePin));
      const deviceTokenSnapshot = await getDocs(deviceTokenQuery);
      
      if (deviceTokenSnapshot.empty) {
        throw new Error(`Device with the ${devicePin} code was not found.`);
      }

      // Call the Cloud Function to create custom token
      const createCustomTokenFunction = httpsCallable(functions, 'createCustomToken');
      const result = await createCustomTokenFunction();
      
      const data = result.data;
      if (data.success) {
        // Update the device login token with the new token
        const deviceTokenDoc = deviceTokenSnapshot.docs[0];
        await updateDoc(deviceTokenDoc.ref, {
          token: data.data,
          tokenCreated: new Date().getTime()
        });
        
        return {
          success: true,
          token: data.data,
          message: 'Custom token created successfully'
        };
      } else {
        throw new Error(data.message || 'Failed to create custom token');
      }
    } catch (error) {
      console.error('Error creating custom token:', error);
      throw error;
    }
  }
}

// Create singleton instance
const deviceService = new DeviceService();

export default deviceService; 